import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BacktestPanel = ({ strategy, onRunBacktest, isVisible, onToggle }) => {
  const [backtestConfig, setBacktestConfig] = useState({
    startDate: '2024-01-01',
    endDate: '2024-09-21',
    initialCapital: 100000,
    commission: 0.1,
    slippage: 0.05,
    dataSource: 'historical'
  });
  
  const [backtestResults, setBacktestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('config');

  // Mock backtest data
  const mockPerformanceData = [
    { date: '2024-01-01', portfolio: 100000, benchmark: 100000, drawdown: 0 },
    { date: '2024-01-15', portfolio: 102500, benchmark: 101200, drawdown: -1.2 },
    { date: '2024-02-01', portfolio: 98750, benchmark: 99800, drawdown: -3.8 },
    { date: '2024-02-15', portfolio: 105200, benchmark: 102100, drawdown: -0.5 },
    { date: '2024-03-01', portfolio: 108900, benchmark: 103500, drawdown: 0 },
    { date: '2024-03-15', portfolio: 112300, benchmark: 105200, drawdown: 0 },
    { date: '2024-04-01', portfolio: 109800, benchmark: 104800, drawdown: -2.2 },
    { date: '2024-04-15', portfolio: 115600, benchmark: 106900, drawdown: 0 },
    { date: '2024-05-01', portfolio: 118200, benchmark: 108100, drawdown: 0 },
    { date: '2024-05-15', portfolio: 121800, benchmark: 109800, drawdown: 0 },
    { date: '2024-06-01', portfolio: 119400, benchmark: 108900, drawdown: -2.0 },
    { date: '2024-06-15', portfolio: 125300, benchmark: 111200, drawdown: 0 },
    { date: '2024-07-01', portfolio: 128900, benchmark: 112800, drawdown: 0 },
    { date: '2024-07-15', portfolio: 132100, benchmark: 114500, drawdown: 0 },
    { date: '2024-08-01', portfolio: 129700, benchmark: 113200, drawdown: -1.8 },
    { date: '2024-08-15', portfolio: 135400, benchmark: 115900, drawdown: 0 },
    { date: '2024-09-01', portfolio: 138600, benchmark: 117100, drawdown: 0 },
    { date: '2024-09-21', portfolio: 142300, benchmark: 118800, drawdown: 0 }
  ];

  const mockTradeData = [
    { month: 'Jan', wins: 12, losses: 8, profit: 2500 },
    { month: 'Feb', wins: 8, losses: 12, profit: -1250 },
    { month: 'Mar', wins: 15, losses: 5, profit: 3700 },
    { month: 'Apr', wins: 10, losses: 10, profit: 1200 },
    { month: 'May', wins: 14, losses: 6, profit: 3000 },
    { month: 'Jun', wins: 9, losses: 11, profit: -800 },
    { month: 'Jul', wins: 16, losses: 4, profit: 4100 },
    { month: 'Aug', wins: 11, losses: 9, profit: 1800 },
    { month: 'Sep', wins: 13, losses: 7, profit: 2900 }
  ];

  const mockMetrics = {
    totalReturn: 42.3,
    annualizedReturn: 18.7,
    sharpeRatio: 1.34,
    maxDrawdown: -3.8,
    winRate: 62.5,
    totalTrades: 187,
    avgWin: 285.40,
    avgLoss: -198.20,
    profitFactor: 1.44,
    calmarRatio: 4.92,
    volatility: 13.9,
    beta: 0.87
  };

  const handleRunBacktest = async () => {
    setIsRunning(true);
    setActiveTab('results');
    
    // Simulate backtest execution
    setTimeout(() => {
      setBacktestResults({
        performance: mockPerformanceData,
        trades: mockTradeData,
        metrics: mockMetrics,
        executionTime: '2.3s',
        dataPoints: 5847
      });
      setIsRunning(false);
      onRunBacktest?.(backtestConfig);
    }, 2300);
  };

  const tabs = [
    { id: 'config', name: 'Configuration', icon: 'Settings' },
    { id: 'results', name: 'Results', icon: 'BarChart3' },
    { id: 'trades', name: 'Trade Analysis', icon: 'TrendingUp' },
    { id: 'risk', name: 'Risk Metrics', icon: 'Shield' }
  ];

  const renderConfigTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Start Date"
          type="date"
          value={backtestConfig?.startDate}
          onChange={(e) => setBacktestConfig(prev => ({ ...prev, startDate: e?.target?.value }))}
        />
        <Input
          label="End Date"
          type="date"
          value={backtestConfig?.endDate}
          onChange={(e) => setBacktestConfig(prev => ({ ...prev, endDate: e?.target?.value }))}
        />
      </div>

      <Input
        label="Initial Capital ($)"
        type="number"
        value={backtestConfig?.initialCapital}
        onChange={(e) => setBacktestConfig(prev => ({ ...prev, initialCapital: parseFloat(e?.target?.value) }))}
        min="1000"
        step="1000"
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Commission (%)"
          type="number"
          value={backtestConfig?.commission}
          onChange={(e) => setBacktestConfig(prev => ({ ...prev, commission: parseFloat(e?.target?.value) }))}
          min="0"
          max="5"
          step="0.01"
        />
        <Input
          label="Slippage (%)"
          type="number"
          value={backtestConfig?.slippage}
          onChange={(e) => setBacktestConfig(prev => ({ ...prev, slippage: parseFloat(e?.target?.value) }))}
          min="0"
          max="1"
          step="0.01"
        />
      </div>

      <Select
        label="Data Source"
        options={[
          { value: 'historical', label: 'Historical Data' },
          { value: 'tick', label: 'Tick Data' },
          { value: 'synthetic', label: 'Synthetic Data' }
        ]}
        value={backtestConfig?.dataSource}
        onChange={(val) => setBacktestConfig(prev => ({ ...prev, dataSource: val }))}
      />

      <div className="pt-4">
        <Button
          onClick={handleRunBacktest}
          loading={isRunning}
          disabled={!strategy || strategy?.components?.length === 0}
          className="w-full"
          iconName="Play"
          iconPosition="left"
        >
          {isRunning ? 'Running Backtest...' : 'Run Backtest'}
        </Button>
      </div>

      {!strategy?.components?.length && (
        <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-warning">
              Add components to your strategy before running a backtest
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const renderResultsTab = () => {
    if (isRunning) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Running backtest...</p>
            <p className="text-xs text-muted-foreground mt-1">Analyzing {mockMetrics?.totalTrades} potential trades</p>
          </div>
        </div>
      );
    }

    if (!backtestResults) {
      return (
        <div className="text-center py-8">
          <Icon name="BarChart3" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No backtest results available</p>
          <p className="text-xs text-muted-foreground mt-1">Run a backtest to see performance metrics</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Total Return</div>
            <div className="text-lg font-semibold text-success">+{backtestResults?.metrics?.totalReturn}%</div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Sharpe Ratio</div>
            <div className="text-lg font-semibold text-foreground">{backtestResults?.metrics?.sharpeRatio}</div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Max Drawdown</div>
            <div className="text-lg font-semibold text-error">{backtestResults?.metrics?.maxDrawdown}%</div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Win Rate</div>
            <div className="text-lg font-semibold text-foreground">{backtestResults?.metrics?.winRate}%</div>
          </div>
        </div>
        {/* Performance Chart */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Portfolio Performance</h4>
          <div className="h-48 bg-background rounded-lg border border-border p-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={backtestResults?.performance}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short' })}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  formatter={(value, name) => [
                    `$${value?.toLocaleString()}`,
                    name === 'portfolio' ? 'Strategy' : 'Benchmark'
                  ]}
                />
                <Line 
                  type="monotone" 
                  dataKey="portfolio" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="benchmark" 
                  stroke="var(--color-muted-foreground)" 
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Execution Summary */}
        <div className="bg-background rounded-lg p-3 border border-border">
          <h4 className="text-sm font-medium text-foreground mb-2">Execution Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Execution Time:</span>
              <span className="text-foreground font-medium">{backtestResults?.executionTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Data Points:</span>
              <span className="text-foreground font-medium">{backtestResults?.dataPoints?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Trades:</span>
              <span className="text-foreground font-medium">{backtestResults?.metrics?.totalTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Profit Factor:</span>
              <span className="text-foreground font-medium">{backtestResults?.metrics?.profitFactor}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTradesTab = () => {
    if (!backtestResults) {
      return (
        <div className="text-center py-8">
          <Icon name="TrendingUp" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No trade data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Trade Statistics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Average Win</div>
            <div className="text-lg font-semibold text-success">+${backtestResults?.metrics?.avgWin}</div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="text-xs text-muted-foreground">Average Loss</div>
            <div className="text-lg font-semibold text-error">${backtestResults?.metrics?.avgLoss}</div>
          </div>
        </div>
        {/* Monthly Trade Analysis */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Monthly Trade Analysis</h4>
          <div className="h-48 bg-background rounded-lg border border-border p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={backtestResults?.trades}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="wins" fill="var(--color-success)" />
                <Bar dataKey="losses" fill="var(--color-error)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Trade Distribution */}
        <div className="bg-background rounded-lg p-3 border border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Trade Distribution</h4>
          <div className="space-y-2">
            {backtestResults?.trades?.slice(0, 5)?.map((trade, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{trade?.month}</span>
                <div className="flex items-center gap-2">
                  <span className="text-success">{trade?.wins}W</span>
                  <span className="text-error">{trade?.losses}L</span>
                  <span className={`font-medium ${trade?.profit >= 0 ? 'text-success' : 'text-error'}`}>
                    ${trade?.profit >= 0 ? '+' : ''}{trade?.profit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderRiskTab = () => {
    if (!backtestResults) {
      return (
        <div className="text-center py-8">
          <Icon name="Shield" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No risk data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Volatility</span>
              <span className="text-sm font-medium text-foreground">{backtestResults?.metrics?.volatility}%</span>
            </div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Beta</span>
              <span className="text-sm font-medium text-foreground">{backtestResults?.metrics?.beta}</span>
            </div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Calmar Ratio</span>
              <span className="text-sm font-medium text-foreground">{backtestResults?.metrics?.calmarRatio}</span>
            </div>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Annualized Return</span>
              <span className="text-sm font-medium text-success">+{backtestResults?.metrics?.annualizedReturn}%</span>
            </div>
          </div>
        </div>
        {/* Risk Assessment */}
        <div className="bg-background rounded-lg p-3 border border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Risk Assessment</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Overall Risk Level</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-1/2 h-full bg-warning" />
                </div>
                <span className="text-xs font-medium text-warning">Medium</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Drawdown Risk</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-1/3 h-full bg-success" />
                </div>
                <span className="text-xs font-medium text-success">Low</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Volatility Risk</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="w-2/3 h-full bg-warning" />
                </div>
                <span className="text-xs font-medium text-warning">Medium</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isVisible) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        className="fixed bottom-4 right-4 z-10"
        iconName="BarChart3"
        iconPosition="left"
      >
        Backtest
      </Button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 w-96 h-96 bg-card border border-border rounded-tl-lg shadow-elevated z-20">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Strategy Backtest</h3>
        <Button variant="ghost" size="sm" onClick={onToggle}>
          <Icon name="X" size={16} />
        </Button>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={12} />
            {tab?.name}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'config' && renderConfigTab()}
        {activeTab === 'results' && renderResultsTab()}
        {activeTab === 'trades' && renderTradesTab()}
        {activeTab === 'risk' && renderRiskTab()}
      </div>
    </div>
  );
};

export default BacktestPanel;